import React, { useState, useEffect, useCallback, memo } from 'react';
import api from '../services/api';
import './Cidades.css';

const Cidades = () => {
  const [cidades, setCidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    sigla: '',
    descricao: '',
    estado: '',
    status: 'ativo'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cidades');

      if (response.data.success) {
        setCidades(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await api.put(`/cidades/${editing.id_cidade}`, formData);
        setSuccess('Cidade atualizada com sucesso!');
      } else {
        await api.post('/cidades', formData);
        setSuccess('Cidade criada com sucesso!');
      }

      setShowModal(false);
      setEditing(null);
      setFormData({
        sigla: '',
        descricao: '',
        estado: '',
        status: 'ativo'
      });
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar cidade');
    }
  };

  const handleEdit = (cidade) => {
    setEditing(cidade);
    setFormData({
      sigla: cidade.sigla,
      descricao: cidade.descricao,
      estado: cidade.estado || '',
      status: cidade.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta cidade?')) {
      return;
    }

    try {
      await api.delete(`/cidades/${id}`);
      setSuccess('Cidade excluída com sucesso!');
      carregarDados();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao excluir cidade');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditing(null);
    setFormData({
      sigla: '',
      descricao: '',
      estado: '',
      status: 'ativo'
    });
  };

  if (loading) {
    return (
      <div className="cidades-container">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="cidades-container">
      <div className="cidades-header">
        <h1>Cidades</h1>
        <button 
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          + Nova Cidade
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="cidades-table-container">
        <table className="cidades-table">
          <thead>
            <tr>
              <th>Sigla</th>
              <th>Descrição</th>
              <th>Estado</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {cidades.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  Nenhuma cidade cadastrada
                </td>
              </tr>
            ) : (
              cidades.map((cidade) => (
                <tr key={cidade.id_cidade}>
                  <td><strong>{cidade.sigla}</strong></td>
                  <td>{cidade.descricao}</td>
                  <td>{cidade.estado || '-'}</td>
                  <td>
                    <span className={`status-badge status-${cidade.status}`}>
                      {cidade.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(cidade)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(cidade.id_cidade)}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Editar Cidade' : 'Nova Cidade'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label htmlFor="sigla">Sigla *</label>
                <input
                  type="text"
                  id="sigla"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value.toUpperCase() })}
                  required
                  maxLength={20}
                  className="form-input"
                  placeholder="Ex: LOREN"
                />
              </div>

              <div className="form-group">
                <label htmlFor="descricao">Descrição *</label>
                <input
                  type="text"
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  required
                  maxLength={150}
                  className="form-input"
                  placeholder="Ex: Lorena - SP"
                />
              </div>

              <div className="form-group">
                <label htmlFor="estado">Estado</label>
                <input
                  type="text"
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value.toUpperCase() })}
                  maxLength={2}
                  className="form-input"
                  placeholder="Ex: SP"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="form-input"
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cidades;

